import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ClipboardPaste, Sparkles, Wand2 } from 'lucide-react';
import { apiPost } from '@/lib/api';

type SourceType = 'gm_call' | 'email' | 'meeting' | 'other';
type EventType = 'note' | 'viewing' | 'video' | 'schedule';

type DraftItem = {
  playerId: string;
  displayName: string;
  currentTeam: string | null;
  currentLeague: string | null;
  classification: string;
  matchedAliases: string[];
  confidence: 'high' | 'medium' | 'low';
  title: string;
  body: string;
  type: EventType;
  occurredAt: number;
  metadataJson: string;
};

type DraftRow = DraftItem & {
  selected: boolean;
};

type PreviewResponse = {
  items: DraftItem[];
  unmatchedText: string | null;
  rosterCount: number;
  parser?: 'ai' | 'fallback' | 'disabled';
  parserModel?: string | null;
};

type ApplyResponse = {
  batchId: string;
  created: number;
  events?: Array<{ eventId: string; playerId: string; title: string }>;
};

const EXAMPLE = `Call with Dallas GM. They like Bobby McMann as a middle-six fit if the number lands in the right range. They asked where he is mentally and whether he would be open to term.

On Alex Tuch, they view him as a premium option and wanted a read on availability. They are not there yet but want to stay close.

Arber Xhekaj came up as a player they value for identity. They asked about comfort level, role, and what a clean landing spot would need to look like.`;

export function BatchNotes() {
  const [sourceType, setSourceType] = useState<SourceType>('gm_call');
  const [eventType, setEventType] = useState<EventType>('note');
  const [sourceTitle, setSourceTitle] = useState('');
  const [note, setNote] = useState('');
  const [occurredDate, setOccurredDate] = useState(todayInputValue());
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [rosterCount, setRosterCount] = useState<number | null>(null);
  const [parser, setParser] = useState<PreviewResponse['parser']>(undefined);
  const [parserModel, setParserModel] = useState<string | null>(null);
  const [unmatchedText, setUnmatchedText] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<ApplyResponse | null>(null);

  const selectedRows = useMemo(() => rows.filter((row) => row.selected && row.title.trim()), [rows]);
  const canPreview = note.trim().length >= 12 && !previewing;
  const canApply = selectedRows.length > 0 && !applying;

  async function preview() {
    if (!canPreview) return;
    setPreviewing(true);
    setError(null);
    setApplied(null);
    try {
      const result = await apiPost<PreviewResponse>('/api/batch-notes/preview', {
        sourceTitle: sourceTitle.trim() || null,
        sourceType,
        eventType,
        note,
        occurredAt: dateInputToUnix(occurredDate),
      });
      setRows(result.items.map((item) => ({ ...item, selected: item.confidence !== 'low' })));
      setRosterCount(result.rosterCount);
      setParser(result.parser);
      setParserModel(result.parserModel ?? null);
      setUnmatchedText(result.unmatchedText);
    } catch {
      setError('Could not draft batch notes. Check the text and try again.');
    } finally {
      setPreviewing(false);
    }
  }

  async function apply() {
    if (!canApply) return;
    const confirmed = window.confirm(`Add ${selectedRows.length} note${selectedRows.length === 1 ? '' : 's'} to player timelines?`);
    if (!confirmed) return;
    setApplying(true);
    setError(null);
    try {
      const result = await apiPost<ApplyResponse>('/api/batch-notes/apply', {
        sourceTitle: sourceTitle.trim() || null,
        sourceType,
        sourceText: note,
        items: selectedRows.map((row) => ({
          playerId: row.playerId,
          title: row.title,
          body: row.body,
          type: row.type,
          occurredAt: row.occurredAt,
          metadataJson: row.metadataJson,
        })),
      });
      setApplied(result);
    } catch {
      setError('Could not apply batch notes. Nothing was written.');
    } finally {
      setApplying(false);
    }
  }

  function updateRow(index: number, patch: Partial<DraftRow>) {
    setRows((current) => current.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));
  }

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-bartlett">Batch Entry</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-ink">Notes Hopper</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted">
            Paste a GM call recap or email summary once, review the drafted player notes, then apply them to the right timelines.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setNote(EXAMPLE);
            setSourceTitle('GM market conversation');
          }}
          className="inline-flex h-10 items-center gap-2 rounded-control border border-border bg-white px-3 text-sm font-semibold text-ink shadow-sm hover:border-bartlett/40"
        >
          <ClipboardPaste size={16} />
          Example
        </button>
      </header>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-4 rounded-control border border-border bg-white p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Source</span>
              <select
                value={sourceType}
                onChange={(event) => setSourceType(event.target.value as SourceType)}
                className="h-10 rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
              >
                <option value="gm_call">GM call</option>
                <option value="email">Email summary</option>
                <option value="meeting">Meeting</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="grid gap-1">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Timeline Type</span>
              <select
                value={eventType}
                onChange={(event) => setEventType(event.target.value as EventType)}
                className="h-10 rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
              >
                <option value="note">Note</option>
                <option value="viewing">Viewing</option>
                <option value="video">Video</option>
                <option value="schedule">Schedule</option>
              </select>
            </label>
          </div>

          <label className="grid gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Title</span>
            <input
              value={sourceTitle}
              onChange={(event) => setSourceTitle(event.target.value)}
              placeholder="GM market conversation, agent email recap, college interest update"
              className="h-10 rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
            />
          </label>

          <label className="grid gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Date</span>
            <input
              type="date"
              value={occurredDate}
              onChange={(event) => setOccurredDate(event.target.value)}
              className="h-10 rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10 sm:w-56"
            />
          </label>

          <label className="grid gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Conversation Summary</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={14}
              placeholder="Paste the call recap or Claude/email summary here. Use player names naturally."
              className="min-h-[320px] w-full resize-y rounded-control border border-border px-3 py-2 text-sm leading-6 outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
            />
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-muted">
              {rosterCount ? `${rosterCount} active players checked. ${parserLabel(parser, parserModel)}` : 'Active roster names are checked during preview.'}
            </div>
            <button
              type="button"
              onClick={preview}
              disabled={!canPreview}
              className="inline-flex h-10 items-center gap-2 rounded-control border border-bartlett bg-bartlett px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Wand2 size={16} />
              {previewing ? 'Drafting...' : 'Draft notes'}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-control border border-border bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-ink">Drafted timeline deposits</h2>
                <p className="mt-1 text-sm text-muted">{selectedRows.length} selected of {rows.length} drafted. {parserLabel(parser, parserModel)}</p>
              </div>
              <button
                type="button"
                onClick={apply}
                disabled={!canApply}
                className="inline-flex h-10 items-center gap-2 rounded-control border border-bartlett bg-bartlett px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles size={16} />
                {applying ? 'Applying...' : 'Apply selected'}
              </button>
            </div>

            {error && (
              <div className="mt-4 flex gap-2 rounded-control border border-flag-critical/30 bg-flag-critical/5 p-3 text-sm text-flag-critical">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {applied && (
              <div className="mt-4 flex gap-2 rounded-control border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <span>Created {applied.created} timeline note{applied.created === 1 ? '' : 's'}.</span>
              </div>
            )}

            {unmatchedText && (
              <div className="mt-4 rounded-control border border-border bg-bg p-3 text-sm text-muted">
                No active player names were matched. Try adding full player names or one player per line.
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="rounded-control border border-dashed border-border bg-white p-6 text-sm leading-6 text-muted">
              Drafted notes will appear here before anything is written to a player file.
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, index) => (
                <DraftCard key={`${row.playerId}-${index}`} row={row} onChange={(patch) => updateRow(index, patch)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DraftCard({ row, onChange }: { row: DraftRow; onChange: (patch: Partial<DraftRow>) => void }) {
  return (
    <article className="rounded-control border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <label className="flex min-w-0 items-start gap-3">
          <input
            type="checkbox"
            checked={row.selected}
            onChange={(event) => onChange({ selected: event.target.checked })}
            className="mt-1 h-4 w-4 rounded border-border text-bartlett focus:ring-bartlett"
          />
          <span className="min-w-0">
            <Link to={`/people/${row.playerId}?tab=timeline`} className="truncate text-sm font-semibold text-ink hover:text-bartlett">
              {row.displayName}
            </Link>
            <span className="mt-0.5 block text-xs text-muted">
              {[row.currentTeam, row.currentLeague].filter(Boolean).join(' / ') || row.classification}
            </span>
          </span>
        </label>
        <span className={confidenceClass(row.confidence)}>
          {row.confidence}
        </span>
      </div>

      <div className="mt-3 grid gap-3">
        <input
          value={row.title}
          onChange={(event) => onChange({ title: event.target.value })}
          className="h-10 rounded-control border border-border px-3 text-sm font-semibold outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
        />
        <textarea
          value={row.body}
          onChange={(event) => onChange({ body: event.target.value })}
          rows={5}
          className="w-full resize-y rounded-control border border-border px-3 py-2 text-sm leading-6 outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {row.matchedAliases.map((alias) => (
          <span key={alias} className="rounded-full border border-border bg-bg px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-muted">
            {alias}
          </span>
        ))}
      </div>
    </article>
  );
}

function confidenceClass(confidence: DraftRow['confidence']) {
  const base = 'rounded-full border px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide';
  if (confidence === 'high') return `${base} border-green-200 bg-green-50 text-green-800`;
  if (confidence === 'medium') return `${base} border-bartlettGold/40 bg-bartlettGold/10 text-bartlett`;
  return `${base} border-flag-critical/30 bg-flag-critical/5 text-flag-critical`;
}

function parserLabel(parser: PreviewResponse['parser'], model: string | null) {
  if (parser === 'ai') return `AI parse${model ? ` (${model})` : ''}.`;
  if (parser === 'disabled') return 'AI parse disabled; name matching used.';
  if (parser === 'fallback') return 'Name matching used.';
  return '';
}

function todayInputValue(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateInputToUnix(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return Math.floor(Date.now() / 1000);
  const [yearRaw, monthRaw, dayRaw] = value.split('-');
  const year = Number.parseInt(yearRaw ?? '', 10);
  const month = Number.parseInt(monthRaw ?? '', 10);
  const day = Number.parseInt(dayRaw ?? '', 10);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  if (Number.isNaN(date.getTime())) return Math.floor(Date.now() / 1000);
  return Math.floor(date.getTime() / 1000);
}
